import { action, computed, observable } from 'mobx';

import { Model, ModelSaveOptions } from 'core/model/Model';
import $lookups from 'app/stores/$lookups';
import { SITE_TYPES } from 'app/util/constants';
import { SiteProps } from '@kentik/ui-shared/types/sites';
import type { DeviceStore } from 'app/stores/device/$devices';

const SITE_LOCATION_FIELDS = ['address', 'city', 'region', 'postal', 'countryName'] as const;

type SiteLocationField = (typeof SITE_LOCATION_FIELDS)[number];

// Asynchronous load of $devices to ensure no import loop can occur - we aim to delete this ASAP
let $devices: DeviceStore | undefined;
import('app/stores/device/$devices').then(({ default: store }) => {
  $devices = store;
});

class SiteModel extends Model<SiteProps> {
  @observable
  countryName?: string;

  totalCost?: unknown;

  get urlRoot() {
    return '/api/ui/sites';
  }

  get sortValues() {
    return {
      country: (model: SiteModel) => model.countryName
    } as const;
  }

  @computed
  get siteLocation() {
    const location: string[] = [];
    SITE_LOCATION_FIELDS.forEach((field) => {
      const value = (this as Partial<Record<SiteLocationField, string>>)[field] || this.get(field);
      if (value) {
        location.push(value);
      }
    });
    return location.join(', ');
  }

  @computed
  get type() {
    const { type } = this.get('metadata') || {};
    return (type && SITE_TYPES[type]) || 'Other';
  }

  /**
   * @deprecated we aim to kill this ASAP - site device counts now come from the backend (device_count etc)
   */
  @computed
  get devices(): unknown[] {
    const id = this.get('id');
    return $devices?.activeDeviceSummaries?.filter((d) => d.site_id === id) || [];
  }

  @computed
  get numDevices() {
    const deviceCount = this.get('device_count');
    if (!deviceCount) {
      return 'No devices';
    }
    return deviceCount > 1 ? `${deviceCount} Devices` : '1 Device';
  }

  @computed
  get hasCloudDevices() {
    return this.get('cloudDevices');
  }

  @computed
  get siteType() {
    const metadata = this.get('metadata');
    const siteTypes = {
      dataCenter: 'Data Center',
      cloud: 'Cloud',
      branch: 'Branch/Building',
      connectivity: 'Connectivity PoP',
      customer: 'Customer/Partner',
      other: 'Other'
    };

    return (metadata && metadata.type && siteTypes[metadata.type]) || 'Site';
  }

  save(attributes: Partial<SiteProps> = {}, options: ModelSaveOptions) {
    if (attributes.title) {
      attributes.title = attributes.title.trim();
    }

    const onSuccess = (success: SiteProps | void) => {
      this.doLookups();
      return success;
    };

    return super.save({ ...attributes }, options).then(onSuccess);
  }

  @action
  doLookups() {
    const country = this.get('country');

    if (country) {
      $lookups.countries(country).then(
        action((countries: { value: string; label: string }[]) => {
          const countryOption = countries.find((option) => option.value === country);
          this.countryName = countryOption ? countryOption.label : country;
          this.setLastUpdated();
        })
      );
    } else {
      this.countryName = '';
      this.setLastUpdated();
    }
  }

  set(data?: Partial<SiteProps>): this;

  set<K extends string & keyof SiteProps>(fieldName: K, fieldData: SiteProps[K]): this;

  set<K extends string & keyof SiteProps>(
    dataOrFieldName: K | Partial<SiteProps> = {},
    fieldData?: SiteProps[K]
  ): this {
    if (typeof dataOrFieldName === 'string') {
      super.set(dataOrFieldName, fieldData as SiteProps[K]);
    } else {
      super.set(dataOrFieldName);
    }
    this.doLookups();
    return this;
  }
}

export default SiteModel;
